import {z} from 'zod';
import {AiImageEditMode} from 'common/src/modules/contentFeeds/contentFeeds.aiContentTypes';

const usedInputColumnSchema = z.object({
  columnName: z.string().describe('Name of the input column to use'),
});

const imageAiGenerationSetupSchema = z
  .object({
    usedInputColumns: z
      .array(usedInputColumnSchema)
      .optional()
      .nullable()
      .describe(
        'input columns to use for the image prompt generation, Image type input columns will act as reference images in the generation',
      ),
    useSharedIntermediateImage: z
      .boolean()
      .optional()
      .describe('Generate single image and smartly crop for all output sizes'),
  })
  .optional()
  .nullable();

const textGenerationSetupSchema = z
  .object({
    usedInputColumns: z
      .array(usedInputColumnSchema)
      .optional()
      .nullable()
      .describe('Input columns to use or reference for output generation'),
  })
  .optional()
  .nullable();

const imageSmartCropSetupSchema = z
  .object({
    inputColumnName: z.string().optional().nullable(),
    objectDetectPrompt: z
      .string()
      .optional()
      .nullable()
      .describe(
        "Name of object to detect in image source and place in the output image focal area, for example 'car','person'",
      ),
    marginPercent: z
      .number()
      .describe('keep away percentage from the output image focal area boundaries'),
  })
  .optional()
  .nullable();

enum ProductShotTypeSelection {
  Product = 'Product',
  Vehicle = 'Vehicle',
}

const productShotGenerationSetupSchema = z
  .object({
    productImageInputColumnName: z
      .string()
      .optional()
      .nullable()
      .describe(
        'Which input column should be used as the product in the image (must be an image type column)',
      ),
    ScenePromptInputColumnName: z
      .array(usedInputColumnSchema)
      .optional()
      .nullable()
      .describe(
        'input columns to use for generating the prompt for the scene (either this or the scene reference image should be used)',
      ),
    OptionalSceneReferenceImageInputColumnName: z
      .string()
      .optional()
      .nullable()
      .describe(
        'if scene uses reference image, which input column should be used for it (must be image type column)',
      ),
    marginPercent: z
      .number()
      .describe('keep away percentage from the output image focal area boundaries'),
    productType: z.nativeEnum(ProductShotTypeSelection).describe('Type of product shot: Product or Vehicle'),
    useSharedIntermediateImage: z
      .boolean()
      .optional()
      .describe('Generate single image and smartly crop for all output sizes'),
  })
  .optional()
  .nullable();

const imageEditSetupSchema = z
  .object({
    inputImageColumnName: z
      .string()
      .optional()
      .nullable()
      .describe('Which input column provides the source image to edit (must be Image type)'),
    stylePromptInputColumns: z
      .array(usedInputColumnSchema)
      .optional()
      .nullable()
      .describe('Input columns used for reasoning about the style change'),
    editMode: z
      .nativeEnum(AiImageEditMode)
      .optional()
      .describe('Edit mode for the image edit operation. Use one of the AiEditMode enum values.'),
  })
  .optional()
  .nullable();

const generationSetupSchema = z.object({
  imageSmartCrop: imageSmartCropSetupSchema,
  imageAiGeneration: imageAiGenerationSetupSchema,
  productShotGeneration: productShotGenerationSetupSchema,
  textGeneration: textGenerationSetupSchema,
  imageEdit: imageEditSetupSchema,
});

type GenerationSetupToolInput = z.infer<typeof generationSetupSchema>;

export {
  generationSetupSchema,
  ProductShotTypeSelection,
  type GenerationSetupToolInput,
};
